import { useEffect, useRef, useState } from "react";
import { lstr } from "./localization";
import { GENDER_BY_NAME, GENDER_CLASS } from "./gender";
import { GenderNoun } from "./practiceApi";
import { Pup } from "./Pup";
import { Confetti } from "./Confetti";

// Learned languages with gender rounds, and the words that name each gender
// there: articles in German, pronouns in Russian. Mirrors
// practice.GenderLanguages on the backend.
const GENDER_CHOICES: Record<
  string,
  { gender: GenderNoun["gender"]; label: string }[]
> = {
  de: [
    { gender: "masculine", label: "der" },
    { gender: "feminine", label: "die" },
    { gender: "neuter", label: "das" },
  ],
  ru: [
    { gender: "masculine", label: "он" },
    { gender: "feminine", label: "она" },
    { gender: "neuter", label: "оно" },
  ],
};

export const GENDER_ROUND_LANGUAGES = new Set(Object.keys(GENDER_CHOICES));

// GenderRound is a quick der/die/das break between cards: tap the right
// gender for a few nouns the learner already practices.
export function GenderRound({
  l,
  r,
  nouns,
  onDone,
}: {
  l: string;
  r: string;
  nouns: GenderNoun[];
  onDone: () => void;
}) {
  const strings = lstr(l);
  const choices = GENDER_CHOICES[r] ?? [];
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<GenderNoun["gender"] | null>(null);
  const [correct, setCorrect] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const finished = index >= nouns.length;
  const noun = nouns[index];

  const pick = (gender: GenderNoun["gender"]) => {
    if (picked || finished) return;
    setPicked(gender);
    if (gender === noun.gender) setCorrect((count) => count + 1);
    timer.current = setTimeout(
      () => {
        setPicked(null);
        setIndex((i) => i + 1);
      },
      gender === noun.gender ? 600 : 1400,
    );
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const choice = choices[Number(event.key) - 1];
      if (choice) pick(choice.gender);
      if (finished && (event.key === "Enter" || event.key === " ")) onDone();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  if (finished) {
    return (
      <div className="card animate-pop-in flex w-full flex-col items-center gap-3 rounded-3xl px-6 py-9 text-center">
        <div className="relative">
          <Pup
            shape="circle"
            mood={correct === nouns.length ? "happy" : "reading"}
            className="h-20 w-20"
          />
          {correct === nouns.length && <Confetti count={24} />}
        </div>
        <h2 className="mt-2 font-literata text-2xl font-bold text-main-text">
          {strings.gender_round_result
            .replace("{correct}", String(correct))
            .replace("{total}", String(nouns.length))}
        </h2>
        <button
          type="button"
          onClick={onDone}
          className="btn-primary mt-3 px-6 py-3"
        >
          {strings.placement_continue}
        </button>
      </div>
    );
  }

  return (
    <div className="card flex w-full flex-col items-center gap-4 rounded-3xl p-6 sm:p-7">
      <p className="eyebrow">
        {strings.gender_round_title} · {index + 1}/{nouns.length}
      </p>
      <p
        key={index}
        lang={r}
        className={`animate-pop-in py-3 font-literata text-[36px] font-bold leading-tight transition-colors ${picked ? GENDER_CLASS[GENDER_BY_NAME[noun.gender]] : "text-main-text"}`}
      >
        {noun.word}
      </p>
      {noun.meaning && (
        <p lang={l} className="text-secondary-text">
          {noun.meaning}
        </p>
      )}
      <div className="grid grid-cols-3 gap-3 w-full max-w-sm">
        {choices.map((choice) => {
          const isAnswer = picked && choice.gender === noun.gender;
          const isWrongPick =
            picked === choice.gender && choice.gender !== noun.gender;
          return (
            <button
              key={choice.gender}
              type="button"
              lang={r}
              onClick={() => pick(choice.gender)}
              className={`btn rounded-2xl border-2 py-3.5 font-literata text-xl ${
                isAnswer
                  ? "animate-bump border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                  : isWrongPick
                    ? "border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                    : "border-border bg-surface text-main-text hover:bg-cream-dark"
              }`}
            >
              {choice.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
